import EventEmitter from 'events';
import { io, Socket } from 'socket.io-client';

// Simple async delay helper
const delay = (delayTime: number): Promise<boolean> =>
  new Promise((resolve) => {
    window.setTimeout(() => resolve(true), delayTime);
  });

const getRoomEventName = (room: string, eventName: string): string => `${room}:${eventName}`;

interface SocketEvent<T> {
  broadcast_group_key: string;
  event_name: string;
  message: T;
}

interface IJoinEventMessage {
  roomName: string;
  status: boolean;
  message: string;
}

export class SocketIOConnection {
  private static instance: SocketIOConnection | null = null;
  private static connecting = false;

  private readonly socket: Socket;
  private readonly joinEventBus = new EventEmitter();
  private readonly eventEmitter = new EventEmitter();
  private readonly joinedRooms = new Set<string>();

  private constructor(socket: Socket) {
    this.socket = socket;

    socket.on('join-status', (message: string | IJoinEventMessage) => {
      const messageObj: IJoinEventMessage =
        typeof message === 'object' ? message : (JSON.parse(message) as IJoinEventMessage);

      this.joinEventBus.emit(messageObj.roomName, messageObj);
    });

    socket.on('disconnecting', () => {
      console.log('socket disconnecting');
    });

    socket.on('disconnect', () => {
      console.log('socket disconnected');
    });

    socket.on('connect', () => {
      console.log('socket connected');
      this.eventEmitter.emit('reconnected', true);

      // Rejoin rooms after reconnect
      this.joinedRooms.forEach((room) => {
        this.socket.emit('join', JSON.stringify({ room }));
      });
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connect error:', err.message);
    });

    socket.on('error', (err) => {
      console.error('Socket error:', err);
    });

    socket.on('push', (data: SocketEvent<any>) => {
      const roomEventName = getRoomEventName(data.broadcast_group_key, data.event_name);
      this.eventEmitter.emit(roomEventName, data.message);
    });
  }

  public onReconnect(func: () => void): void {
    this.eventEmitter.on('reconnected', func);
  }

  public static getConnection(): Promise<SocketIOConnection> {
    const socketUrl: string = (process.env as any).REACT_APP_SOCKET_URL ?? '';

    const getOrCreateConnection = async (
      resolve: (value: SocketIOConnection) => void
    ): Promise<void> => {
      if (SocketIOConnection.connecting) {
        while (SocketIOConnection.connecting) {
          await delay(1000);
        }

        if (SocketIOConnection.instance !== null) {
          resolve(SocketIOConnection.instance);
        }
        return;
      }

      if (SocketIOConnection.instance === null) {
        SocketIOConnection.connecting = true;

        const socket = io(socketUrl, {
          transports: ['websocket'],
          upgrade: false,
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000
        });

        socket.on('connect', () => {
          SocketIOConnection.instance = new SocketIOConnection(socket);
          SocketIOConnection.connecting = false;
          resolve(SocketIOConnection.instance);
        });
      } else {
        resolve(SocketIOConnection.instance);
      }
    };

    return new Promise((resolve) => {
      void getOrCreateConnection(resolve);
    });
  }

  public joinRoom(roomName: string, authKey: unknown): Promise<string> {
    this.joinedRooms.add(roomName);

    return new Promise((resolve, reject) => {
      this.socket.emit(
        'join',
        JSON.stringify({
          auth_key: authKey,
          room: roomName
        })
      );

      this.joinEventBus.once(roomName, (data: IJoinEventMessage) => {
        if (data.status) {
          resolve(data.message);
        } else {
          reject(data.message);
        }
      });
    });
  }

  public addEventListener<T>(
    roomName: string,
    eventName: string,
    callback: (message: T) => void
  ): void {
    const roomEventName = getRoomEventName(roomName, eventName);
    this.eventEmitter.on(roomEventName, callback);
  }

  public removeListener(roomName: string, eventName: string): void {
    const roomEventName = getRoomEventName(roomName, eventName);
    this.eventEmitter.removeAllListeners(roomEventName);
  }

  public disconnect(): void {
    this.socket.disconnect();
    SocketIOConnection.instance = null;
    SocketIOConnection.connecting = false;
    this.joinedRooms.clear();
  }
}
